/**
 * AuthProvider state + behavior, decomposed into focused hooks so no single
 * function exceeds the size/complexity gates. `useAuthSession` composes them into
 * the AuthContextType value consumed by <AuthContext.Provider>.
 *
 * `logout` reads `auth_tokens` from localStorage directly — this is part of the
 * auth bootstrap the lint guard carves out (see eslint.config.js).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AuthError,
  type AuthContextType,
  type AuthTokens,
  type LoginCredentials,
  type RegisterData,
  type RegistrationCapability,
  type User,
} from "./AuthContext";
import {
  blacklistRefreshToken,
  fetchRegistrationCapability,
  fetchUserProfileForLogin,
  registerUser,
  requestTokenRefresh,
  requestTokens,
} from "./authApi";
import { runAuthBootstrap } from "./authBootstrap";
import { storyQueryKeys } from "@/queries/storyQueryKeys";
import { clearPlannerBootstrapInFlightRequests } from "@/api/plannerBootstrap";
import { useFmToast } from "@/components/ui";

interface SessionSetters {
  setUser: (user: User | null) => void;
  setTokens: (tokens: AuthTokens | null) => void;
  setIsLoading: (loading: boolean) => void;
}

/**
 * Cross-tab / session-expired sync. Token rotation in one tab must update the
 * other tabs in memory: reloading here tears down active server-state queries
 * and can leave a data-backed route empty while the development or production
 * bundle reconnects.
 */
function useAuthStorageSync({
  setUser,
  setTokens,
  setIsLoading,
  queryClient,
}: SessionSetters & { queryClient: ReturnType<typeof useQueryClient> }): void {
  useEffect(() => {
    const clearInMemoryAuth = () => {
      setUser(null);
      setTokens(null);
      setIsLoading(false);
      queryClient.removeQueries({ queryKey: storyQueryKeys.all });
      clearPlannerBootstrapInFlightRequests();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== "auth_tokens" && event.key !== "auth_user") return;
      if (event.newValue === null) {
        clearInMemoryAuth();
        return;
      }

      if (event.oldValue === event.newValue) return;

      try {
        if (event.key === "auth_tokens") {
          setTokens(JSON.parse(event.newValue) as AuthTokens);
          return;
        }

        setUser(JSON.parse(event.newValue) as User);
        queryClient.removeQueries({ queryKey: storyQueryKeys.all });
        clearPlannerBootstrapInFlightRequests();
      } catch {
        clearInMemoryAuth();
      }
    };

    window.addEventListener(
      "lehti_ai:auth-session-expired",
      clearInMemoryAuth,
    );
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(
        "lehti_ai:auth-session-expired",
        clearInMemoryAuth,
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [queryClient, setUser, setTokens, setIsLoading]);
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  refreshTokens: (refreshToken: string) => Promise<AuthTokens>;
  getAuthHeaders: () => Record<string, string>;
  checkAuth: () => void;
}

function useRegistrationCapabilityState(): {
  registrationCapability: RegistrationCapability | null;
  registrationCapabilityError: boolean;
} {
  const [registrationCapability, setRegistrationCapability] =
    useState<RegistrationCapability | null>(null);
  const [registrationCapabilityError, setRegistrationCapabilityError] =
    useState(false);

  useEffect(() => {
    let active = true;
    fetchRegistrationCapability()
      .then((capability) => {
        if (active) setRegistrationCapability(capability);
      })
      .catch(() => {
        if (active) setRegistrationCapabilityError(true);
      });
    return () => {
      active = false;
    };
  }, []);

  return { registrationCapability, registrationCapabilityError };
}

interface AuthActionsParams extends SessionSetters {
  tokens: AuthTokens | null;
  toast: ReturnType<typeof useFmToast>;
  queryClient: ReturnType<typeof useQueryClient>;
}

function useAuthActions({
  tokens,
  toast,
  setUser,
  setTokens,
  setIsLoading,
  queryClient,
}: AuthActionsParams): AuthActions {
  // Refresh access token using refresh token
  const refreshTokens = async (
    refreshTokenValue: string,
  ): Promise<AuthTokens> => {
    const newTokens = await requestTokenRefresh(refreshTokenValue);
    setTokens(newTokens);
    localStorage.setItem("auth_tokens", JSON.stringify(newTokens));
    return newTokens;
  };

  // Login function
  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      setIsLoading(true);
      const newTokens = await requestTokens(credentials);

      // Profile fetch must succeed for the session to be considered established.
      // NO FALLBACK: a synthetic user (id=1, user_type=standard, is_active_customer=true)
      // would silently corrupt export metadata, audit trails, and per-tier display.
      // if the profile endpoint is unavailable, fail the login
      // and clear tokens — the user can retry rather than operate against fake data.
      let userData: User;
      try {
        userData = await fetchUserProfileForLogin(newTokens.access);
      } catch (error) {
        if (error instanceof AuthError) {
          throw error;
        }
        throw new AuthError(
          "PROFILE_FETCH_FAILED",
          error instanceof Error ? error.message : "Profile fetch failed",
        );
      }

      await queryClient.cancelQueries({ queryKey: storyQueryKeys.all });
      queryClient.removeQueries({ queryKey: storyQueryKeys.all });
      clearPlannerBootstrapInFlightRequests();

      setUser(userData);
      localStorage.setItem("auth_user", JSON.stringify(userData));

      setTokens(newTokens);
      localStorage.setItem("auth_tokens", JSON.stringify(newTokens));

      toast.success("Login successful!");
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        "NETWORK_ERROR",
        error instanceof Error ? error.message : "Network error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Register function
  const register = async (data: RegisterData): Promise<void> => {
    try {
      setIsLoading(true);
      await registerUser(data);
      // Success feedback is intentionally shown by the calling component, not
      // here: AuthProvider is mounted above LanguageProvider, so this layer has
      // no access to the i18n `t` object and could only emit a hardcoded string.
      // The register forms (which DO have i18n) surface the verification-required
      // state — EnhancedRegisterForm renders a verification-pending panel and
      // RegisterForm shows a localized success message.
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        "NETWORK_ERROR",
        error instanceof Error ? error.message : "Network error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function — best-effort server-side blacklist of the refresh token
  // before clearing local auth state. Local clear runs even if the server call
  // fails (offline, server error) so the user is never trapped in a logged-in
  // UI state.
  const logout = (): void => {
    const storedTokens = localStorage.getItem("auth_tokens");
    let refresh: string | null = null;
    if (storedTokens) {
      try {
        refresh = JSON.parse(storedTokens).refresh ?? null;
      } catch {
        refresh = null;
      }
    }

    if (refresh) {
      blacklistRefreshToken(refresh);
    }

    setUser(null);
    setTokens(null);
    localStorage.removeItem("auth_tokens");
    localStorage.removeItem("auth_user");
    clearPlannerBootstrapInFlightRequests();
    // Invalidate React Query caches (property subscriptions and other shared queries)
    // to prevent the next user from seeing the previous user's cached data within staleTime.
    queryClient.removeQueries({ queryKey: storyQueryKeys.all });
    toast.info("Logged out successfully");
  };

  // Refresh token function
  const refreshToken = async (): Promise<void> => {
    if (!tokens?.refresh) {
      throw new Error("No refresh token available");
    }

    try {
      await refreshTokens(tokens.refresh);
    } catch (error) {
      logout();
      throw error;
    }
  };

  // Get authorization headers
  const getAuthHeaders = (): Record<string, string> => {
    if (!tokens?.access) {
      return {};
    }

    return {
      Authorization: `Bearer ${tokens.access}`,
    };
  };

  // Check authentication status
  const checkAuth = (): void => {
    // This is called by ProtectedRoute to trigger auth check
    // The actual check happens in useEffect on mount
  };

  return {
    login,
    register,
    logout,
    refreshToken,
    refreshTokens,
    getAuthHeaders,
    checkAuth,
  };
}

/**
 * Runs the cold-load bootstrap exactly once on mount. `refreshTokens`/`logout`
 * are read through refs so the effect never re-runs when those closures change,
 * while always invoking the latest version.
 */
function useAuthBootstrap({
  setUser,
  setTokens,
  setIsLoading,
  refreshTokens,
  logout,
}: SessionSetters & Pick<AuthActions, "refreshTokens" | "logout">): void {
  const refreshTokensRef = useRef(refreshTokens);
  refreshTokensRef.current = refreshTokens;
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    runAuthBootstrap({
      setUser,
      setTokens,
      setIsLoading,
      refreshTokens: (refreshToken: string) =>
        refreshTokensRef.current(refreshToken),
      logout: () => logoutRef.current(),
    });
  }, [setUser, setTokens, setIsLoading]);
}

export function useAuthSession(): AuthContextType {
  const toast = useFmToast();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { registrationCapability, registrationCapabilityError } =
    useRegistrationCapabilityState();

  const actions = useAuthActions({
    tokens,
    toast,
    setUser,
    setTokens,
    setIsLoading,
    queryClient,
  });

  useAuthStorageSync({ setUser, setTokens, setIsLoading, queryClient });
  useAuthBootstrap({
    setUser,
    setTokens,
    setIsLoading,
    refreshTokens: actions.refreshTokens,
    logout: actions.logout,
  });

  const { login, register, logout, refreshToken, getAuthHeaders, checkAuth } =
    actions;

  return useMemo<AuthContextType>(
    () => ({
      user,
      tokens,
      isAuthenticated: !!user && !!tokens,
      isLoading,
      login,
      register,
      logout,
      refreshToken,
      getAuthHeaders,
      checkAuth,
      registrationCapability,
      registrationCapabilityError,
    }),
    [
      user,
      tokens,
      isLoading,
      login,
      register,
      logout,
      refreshToken,
      getAuthHeaders,
      checkAuth,
      registrationCapability,
      registrationCapabilityError,
    ],
  );
}
